import React from "react";
import { Box, Typography } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";

/**
 * Card-network badge (DoorDash-style): a small rounded plate drawn in the
 * network's own colors. Pure CSS — no image assets to load or break.
 *
 * Lives here (not inside QorSavedCards) because the checkout payment block,
 * the payment sheet and the account page all render the same plate.
 */
export function BrandBadge({ brand, size = "md" }) {
  const b = (brand || "").toLowerCase();
  const small = size === "sm";
  const plate = {
    width: small ? 34 : 46,
    height: small ? 22 : 30,
    borderRadius: "6px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #e5e7eb",
    bgcolor: "#fff",
  };
  if (b.includes("visa")) {
    return (
      <Box sx={plate}>
        <Typography
          sx={{
            fontWeight: 800,
            fontStyle: "italic",
            fontSize: small ? 10 : 13,
            color: "#1434CB",
            letterSpacing: 0.3,
          }}
        >
          VISA
        </Typography>
      </Box>
    );
  }
  if (b.includes("master") || b === "mc") {
    const dot = small ? 12 : 16;
    return (
      <Box sx={plate}>
        <Box sx={{ display: "flex" }}>
          <Box sx={{ width: dot, height: dot, borderRadius: "50%", bgcolor: "#EB001B" }} />
          <Box
            sx={{
              width: dot,
              height: dot,
              borderRadius: "50%",
              bgcolor: "#F79E1B",
              ml: small ? "-5px" : "-7px",
              opacity: 0.9,
            }}
          />
        </Box>
      </Box>
    );
  }
  if (b.includes("amex") || b.includes("american")) {
    return (
      <Box sx={{ ...plate, bgcolor: "#2E77BC", border: "none" }}>
        <Typography sx={{ fontWeight: 800, fontSize: small ? 8 : 10.5, color: "#fff", letterSpacing: 0.4 }}>
          AMEX
        </Typography>
      </Box>
    );
  }
  if (b.includes("discover") || b.includes("disc")) {
    return (
      <Box sx={plate}>
        <Typography sx={{ fontWeight: 800, fontSize: small ? 8 : 10, color: "#111" }}>
          DISC<Box component="span" sx={{ color: "#F76E20" }}>O</Box>VER
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ ...plate, bgcolor: "#f8fafc" }}>
      <CreditCardIcon sx={{ fontSize: small ? 14 : 18, color: "#64748b" }} />
    </Box>
  );
}

export function brandLabel(brand) {
  const b = (brand || "").toLowerCase();
  if (b.includes("visa")) return "Visa";
  if (b.includes("master") || b === "mc") return "Mastercard";
  if (b.includes("amex") || b.includes("american")) return "Amex";
  if (b.includes("discover") || b.includes("disc")) return "Discover";
  return "Card";
}

/** "Visa ....2748" — the way the card reads everywhere in checkout. */
export function cardTitle(card) {
  if (!card) return "";
  return `${brandLabel(card.brand)} ....${card.last4 || "----"}`;
}

/** "Exp. 01/2028" from whatever shape the gateway returned. */
export function cardExpiry(card) {
  if (!card || !card.expiry) return "";
  return `Exp. ${card.expiry}`;
}
